import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useChallenge } from "@/contexts/ChallengeContext";
import { HOME_KEYS } from "./useHomePrefetch";
import { calculateCurrentDay, getEffectiveStartDate, parsePaceMode } from "@/lib/challengeDay";

interface ChallengeWithProgress {
  id: string;
  title: string;
  totalDays: number;
  status: string;
  currentDay: number;
  progress: number;
  challengeType: string;
  isCourseCompleted: boolean;
}

export function useUserChallengesQuery() {
  const { effectiveUserId } = useImpersonation();
  const { currentChallengeId } = useChallenge();

  return useQuery({
    queryKey: [...HOME_KEYS.userChallenges, effectiveUserId, currentChallengeId],
    queryFn: async (): Promise<ChallengeWithProgress[]> => {
      if (!effectiveUserId) return [];

      // שליפת enrollments פעילים + קורסים שהושלמו (תוכן עדיין נגיש)
      const { data: enrollments, error } = await supabase
        .from('user_challenge_enrollments')
        .select(`
          id,
          enrolled_at,
          is_course_completed,
          challenge:challenges(
            id,
            title,
            total_days,
            status,
            start_date,
            challenge_type,
            pace_mode,
            pace_days,
            anchor_day,
            preparation_days
          )
        `)
        .eq('user_id', effectiveUserId)
        .or('completed_at.is.null,is_course_completed.eq.true')
        .order('enrolled_at', { ascending: false })
        .limit(3);

      // Throw, don't return []: an empty list renders "עוד לא הצטרפת לפעילות",
      // which told offline members they had never joined anything.
      if (error) throw error;
      if (!enrollments) return [];

      return enrollments
        .filter(enrollment => enrollment.challenge)
        // Only show the current active challenge/course (1 at a time)
        .filter(enrollment => enrollment.challenge.id === currentChallengeId)
        .map(enrollment => {
          const ch = enrollment.challenge;
          const effectiveStart = getEffectiveStartDate(
            { start_date: ch.start_date, challenge_type: ch.challenge_type },
            { enrolled_at: enrollment.enrolled_at }
          );
          const currentDay = calculateCurrentDay(
            effectiveStart,
            ch.preparation_days ?? 2,
            parsePaceMode(ch.pace_mode),
            ch.pace_days ?? undefined,
            ch.total_days,
            ch.anchor_day ?? undefined
          );
          const clamped = Math.max(0, Math.min(currentDay, ch.total_days));
          return {
            id: ch.id,
            title: ch.title,
            totalDays: ch.total_days,
            status: ch.status,
            currentDay,
            progress: Math.round((clamped / ch.total_days) * 100),
            challengeType: ch.challenge_type,
            isCourseCompleted: enrollment.is_course_completed,
          };
        });
    },
    enabled: !!effectiveUserId,
    staleTime: 5 * 60 * 1000,
  });
}
